import type { IMessage } from 'react-native-gifted-chat';

import type { ICustomMessage } from '@/components/chat';
import type { ClinicalCase } from '@/types/clinicalCase';

/**
 * Puts the inquiry's clinical case on its opening message, the one with the lowest id, so the
 * bubble that shows it re-renders when the case changes. Left alone while earlier pages are
 * unloaded: the oldest message in hand is then not the opening one, and the case would land on
 * the wrong bubble.
 */
export function withClinicalCase<T extends IMessage>(
  messages: T[],
  clinicalCase: ClinicalCase | undefined,
  hasEarlierMessages: boolean | undefined
): T[] {
  if (!clinicalCase || hasEarlierMessages) {
    return messages;
  }

  let opening: T | undefined;

  for (const message of messages) {
    if (!message.text?.trim() || typeof message._id !== 'number') {
      continue;
    }

    if (!opening || message._id < (opening._id as number)) {
      opening = message;
    }
  }

  if (!opening) {
    return messages;
  }

  return messages.map((message) => (message === opening ? { ...message, clinicalCase } : message));
}
